// Drawer de alertas globales (round 2026-05-06).
//
// Muestra dos secciones:
//   - 🔴 Vencidas: tareas con terminadoEl < hoy y estado != finalizado.
//   - 🔁 Próximas recurrencias: tareas-plantilla con proximaEl en los
//     próximos 7 días (incluye hoy y atrasadas).
//
// Click en un item navega al espacio/categoría correspondiente y resalta
// la fila. Backend ya filtra por rol — el drawer NO necesita lógica de
// permisos.

import React from "react";
import { Drawer, Spin, Empty, Tag } from "antd";
import dayjs from "dayjs";
import type { Task } from "../types";
import { ahoraChile } from "../../../utils/fechaChile";

interface AlertasDrawerProps {
  open: boolean;
  onClose: () => void;
  vencidas: Task[];
  recurrentesProximas: Task[];
  loading: boolean;
  onSelectTask?: (task: Task) => void;
}

const itemStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #f0f0f0",
  marginBottom: 8,
  cursor: "pointer",
  background: "#fff",
  transition: "background 0.15s",
};

const AlertasDrawer: React.FC<AlertasDrawerProps> = ({
  open,
  onClose,
  vencidas,
  recurrentesProximas,
  loading,
  onSelectTask,
}) => {
  const today = ahoraChile().format("YYYY-MM-DD");

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="🔔 Alertas y recordatorios"
      width={460}
      placement="right"
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          {/* ── Vencidas ─────────────────────────────────────────────── */}
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 14,
                marginBottom: 8,
                color: "#dc2626",
              }}
            >
              🔴 Vencidas ({vencidas.length})
            </div>
            {vencidas.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Sin tareas vencidas"
              />
            ) : (
              vencidas.map((t) => {
                const desc = (t.descripcion ?? "").trim() || "(sin descripción)";
                const venceFmt = (t as any).terminadoEl
                  ? dayjs((t as any).terminadoEl).format("DD/MM/YY")
                  : "";
                const diasAtraso = (t as any).terminadoEl
                  ? dayjs(today).diff(dayjs((t as any).terminadoEl), "day")
                  : 0;
                return (
                  <div
                    key={`v-${t.id}`}
                    style={{
                      ...itemStyle,
                      borderLeft: "3px solid #dc2626",
                    }}
                    onClick={() => onSelectTask?.(t)}
                  >
                    <div style={{ fontWeight: 500, marginBottom: 4 }}>
                      {desc}
                    </div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      <Tag color="red">
                        Atrasada {diasAtraso} {diasAtraso === 1 ? "día" : "días"}
                      </Tag>
                      {venceFmt && (
                        <span style={{ marginLeft: 4 }}>vencía {venceFmt}</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* ── Recurrentes próximas ─────────────────────────────────── */}
          <div>
            <div
              style={{
                fontWeight: 700,
                fontSize: 14,
                marginBottom: 8,
                color: "#1e40af",
              }}
            >
              🔁 Recurrencias próximas ({recurrentesProximas.length})
            </div>
            {recurrentesProximas.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Sin recurrencias próximas (próximos 7 días)"
              />
            ) : (
              recurrentesProximas.map((t) => {
                const desc = (t.descripcion ?? "").trim() || "(sin descripción)";
                const rec = (t as any).recurrencia;
                const proxima = rec?.proximaEl;
                const fmt = proxima ? dayjs(proxima).format("DD/MM/YY") : "";
                const diff = proxima ? dayjs(proxima).diff(dayjs(today), "day") : 0;
                const tagColor =
                  diff < 0 ? "red" : diff === 0 ? "orange" : "blue";
                const tagText =
                  diff < 0
                    ? `Atrasada ${Math.abs(diff)}d`
                    : diff === 0
                      ? "Hoy"
                      : `En ${diff} ${diff === 1 ? "día" : "días"}`;
                return (
                  <div
                    key={`r-${t.id}`}
                    style={{
                      ...itemStyle,
                      borderLeft: "3px solid #1e40af",
                    }}
                    onClick={() => onSelectTask?.(t)}
                  >
                    <div style={{ fontWeight: 500, marginBottom: 4 }}>
                      {desc}
                    </div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      <Tag color={tagColor}>{tagText}</Tag>
                      {fmt && (
                        <span style={{ marginLeft: 4 }}>
                          {rec
                            ? `cada ${rec.cada} ${rec.tipo}${rec.cada > 1 ? "s" : ""} — próxima ${fmt}`
                            : fmt}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </Drawer>
  );
};

export default AlertasDrawer;
